import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { CommonService } from "./common.service";
import { catchError, map } from "rxjs/operators";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  messageQueue = [];
  processingMessage = false;
  excludedUrlsRegex: RegExp[];
  excludedUrls = ['.svg'] ;

  constructor(
    private commonService: CommonService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {
    this.excludedUrlsRegex =
      this.excludedUrls.map(urlPattern => new RegExp(urlPattern, 'i')) || [];
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const passThrough: boolean =
      !!this.excludedUrlsRegex.find(regex => regex.test(req.url));

    if (passThrough) {
      return next.handle(req);
    }

    let headers = req.headers;
    headers = headers.append('Content-Type', 'application/json');
    headers = headers.append('Client-Time-Zone', Intl.DateTimeFormat().resolvedOptions().timeZone);

    const body = req.body;
    let requestToSend;

    if (this.commonService.isAuthenticated()) {
      const authenticationToken = this.commonService.getAuthenticationToken();
      headers = headers.append('Authorization', 'Bearer ' + authenticationToken);
    } else if (this.commonService.isPasswordTokenPresent()) {
      const resetPasswordToken = this.commonService.getPasswordToken();
      headers = headers.append('Authorization', 'Bearer ' + resetPasswordToken);
    } else if (this.commonService.isRegisterTokenPresent()) {
      const registerToken = this.commonService.getRegisterToken();
      headers = headers.append('Authorization', 'Bearer ' + registerToken);
    } else if (this.commonService.isRegisterOtpTokenPresent()) {
      const registerOtpToken = this.commonService.getRegisterOtpToken();
      headers = headers.append('Authorization', 'Bearer ' + registerOtpToken);
    } else if (this.commonService.isOnboardingTokenPresent()) {
      const onboardingToken = this.commonService.getOnboardingToken();
      headers = headers.append('Authorization', 'Bearer ' + onboardingToken);
    } else if (this.commonService.isLoginOTPTokenPresent()) {
      const twoFactorAuthenticationToken = this.commonService.getLoginOTPToken();
      headers = headers.append('Authorization', 'Bearer ' + twoFactorAuthenticationToken);
    }

    if (this.commonService.is2FATPresent()) {
      headers = headers.append('tfa', this.commonService.get2FAT() || null);
    }


    requestToSend = req.clone({headers, body});
    return next.handle(requestToSend).pipe(
      map((response) => {
        if (
          (response['body'] && !response['body'].success) ||
          ('success' in response && response['success'] === false)
        ) {
          throw response;
        }

        return response;
      }),
      catchError((errorResponse) => this.handleError(errorResponse))
    );
  }

  handleError(response) {
    if (response.status === 401) {
      this.commonService.clearAppState();
      this.commonService.clearStorage();
      this.router.navigate(['/']);
    }

    const errorObject = (response && response.error) || (response && response.body) || {
      message: 'Oops! Something went wrong.',
    };

    if (errorObject.validationErrorStack) {
      errorObject.validationErrorStack.forEach(eachValidationError => {
        this.addMessageToQueue(eachValidationError.message);
      })
    } else {
      this.addMessageToQueue(errorObject.message);
    }

    return throwError(errorObject);
  }

  addMessageToQueue(message) {
    this.messageQueue.push(message);

    if (!this.processingMessage) {
      this.displaySnackbar();
    }
  }

  displaySnackbar() {
    const nextMessage = this.getNextMessage();

    if (!nextMessage) {
      this.processingMessage = false; // No message in the queue, set flag false
      return;
    }

    this.processingMessage = true; // A message was dequeue and is being processed

    this.snackBar
      .open(nextMessage, undefined, {
        duration: 1500,
        verticalPosition: 'bottom', // 'top' | 'bottom'
        horizontalPosition: 'end', // 'start' | 'center' | 'end' | 'left' | 'right'
        panelClass: 'danger',
      })
      .afterDismissed()
      .subscribe(() => {
        this.displaySnackbar();
      });
  }

  getNextMessage(): string | undefined {
    return this.messageQueue.length ? this.messageQueue.shift() : null;
  }
}
